<!--
VueButtonsList Component - Vertical Button List Selection Component

INTERCHANGEABLE WITH: SelectList.vue
- Same props interface
- Same emit interface
- Same v-model support
- Same auto-select behavior
- Drop-in replacement - just swap component names

FEATURES:
=========
- Vertical list of buttons for selection
- Auto-selects first available option when no value is set
- Supports validation with visual indicators
- Handles disabled state
- Supports required field validation
- Emits both v-model and legacy input events
- Responsive layout with label

PROPS:
======
- itensSelect: Array of objects with {ID, Name} structure
- elemId: Unique identifier for the button list element
- placeholder: Placeholder (not used in button list, kept for compatibility)
- label: Label text displayed on the left
- validation: Function that returns boolean for validation state
- ngRequired: Boolean/string indicating if field is required
- disabled: Boolean/string indicating if field is disabled
- modelValue: Current selected value (v-model)
- value: Legacy value prop for backward compatibility
- callback: Optional function to call when value changes
- searchEnabled: Boolean indicating if search bar should be displayed (default: false)
-->

<template>
  <div class="button-list-container" :class="{ 'no-label': !hasLabel }">
    <div v-if="hasLabel" class="button-list-label" :class="{
      'validation-warning': !hasValidation,
      'validation-success': hasValidation
    }">
      <label class="field-label" :class="{ 'label-disabled': isDisabled }">
        <i v-if="isRequired" class="pi pi-exclamation-triangle validation-icon-warning" v-show="!hasValidation"></i>
        <i v-if="isRequired" class="pi pi-check validation-icon-success" v-show="hasValidation"></i>
        <span v-if="isRequired" class="required-indicator">*</span>{{ label }}:
      </label>
    </div>
    <div class="button-list-content" :id="'container_' + elemId">
      <div v-if="searchEnabled" class="search-bar-container">
        <IconField iconPosition="left">
          <InputIcon>
            <i class="pi pi-search" />
          </InputIcon>
          <InputText
            v-model="searchQuery"
            :placeholder="t('Procurar')"
            class="search-input"
            :disabled="isDisabled"
          />
        </IconField>
      </div>
      <Button
        v-for="item in filteredItems"
        :key="item.ID"
        type="button"
        :label="item.Name"
        :icon="item.ID === internalValue ? 'pi pi-check' : ''"
        :severity="item.ID === internalValue ? 'primary' : 'secondary'"
        :outlined="item.ID !== internalValue"
        :disabled="isDisabled"
        :data-testid="`${elemId}-${item.ID}`"
        @click="onChangeSelect(item.ID)"
        class="button-list-item"
      />
      <div v-if="searchEnabled && filteredItems.length === 0" class="no-results">
        {{ t('No results found') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';

interface SelectItem {
  ID: string | number;
  Name: string;
}

const { t } = useI18n();

const props = defineProps({
  // Optional callback function triggered when selection changes
  callback: Function as any,

  // Array of objects with {ID, Name} structure for button options
  itensSelect: {
    type: Array as () => SelectItem[],
    default: () => []
  },

  // Unique identifier for the button list element
  elemId: String,

  // Placeholder (kept for API compatibility with SelectList, not used in buttons)
  placeholder: String,

  // Label text displayed to the left of the buttons
  label: String,

  // Validation function that returns boolean (true = valid, false = invalid)
  validation: {
    type: Function as any,
    default: () => true
  },

  // Indicates if the field is required (shows warning icon when invalid)
  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  // Indicates if the field should be disabled
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // Current selected value (v-model support)
  modelValue: [String, Number] as any,

  // Legacy value prop for backward compatibility
  value: [String, Number] as any,

  // Enable search bar to filter options
  searchEnabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  'input': [value: string | number];
}>();

// Reactive data
const internalValue = ref<string | number>(props.modelValue ?? props.value ?? '');
const searchQuery = ref<string>('');

// Computed properties
const hasLabel = computed((): boolean => {
  return props.label !== null &&
         props.label !== undefined &&
         props.label !== '' &&
         (props.label as string).trim() !== '';
});

const filteredItems = computed((): SelectItem[] => {
  if (!props.searchEnabled || !searchQuery.value) {
    return props.itensSelect;
  }

  const query = searchQuery.value.toLowerCase();
  return props.itensSelect.filter((item: SelectItem) =>
    item.Name && item.Name.toLowerCase().includes(query)
  );
});

const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

// Methods
const autoSelectFirstValue = (): void => {
  // Only auto-select if no value is currently selected and there are items available
  if ((!internalValue.value || internalValue.value === '') &&
      props.itensSelect &&
      props.itensSelect.length > 0) {
    // Skip the placeholder option (empty value) and select the first actual item
    const firstItem = props.itensSelect.find((item: SelectItem) => item.ID && item.ID !== '');
    if (firstItem) {
      internalValue.value = firstItem.ID;
    }
  }
};

const onChangeSelect = (id: string | number): void => {
  if (!isDisabled.value) {
    internalValue.value = id;
  }
};

// Watchers
watch(() => props.modelValue, (newVal: string | number | undefined) => {
  internalValue.value = newVal ?? '';
});

watch(() => props.value, (newVal: string | number | undefined) => {
  internalValue.value = newVal ?? '';
});

watch(internalValue, (newVal: string | number) => {
  emit('update:modelValue', newVal);
  emit('input', newVal); // Keep for backward compatibility
  if (props.callback) {
    (props.callback as () => void)();
  }
});

watch(() => props.itensSelect, () => {
  nextTick(() => {
    // Auto-select first value if no value is currently selected
    autoSelectFirstValue();
  });
}, { deep: true });

// Auto-select first value on mount
nextTick(() => {
  autoSelectFirstValue();
});
</script>

<style scoped>
/* Main Container */
.button-list-container {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  align-items: flex-start;
}

/* Container without label - make content full width */
.button-list-container.no-label {
  gap: 0;
}

.button-list-container.no-label .button-list-content {
  width: 100%;
}

/* Label Section */
.button-list-label {
  flex: 0 0 20%;
  min-width: 120px;
  text-align: right;
  padding-top: 5px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.label-disabled {
  opacity: 0.5;
}

.required-indicator {
  color: #f39c12;
  margin-right: 2px;
}

/* Validation Icons */
.validation-icon-warning {
  color: #f39c12;
  font-size: 0.875rem;
}

.validation-icon-success {
  color: #00a65a;
  font-size: 0.875rem;
}

/* Content Section */
.button-list-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* Search Bar */
.search-bar-container {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
}

/* No Results Message */
.no-results {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
}

/* Button Styling */
.button-list-item {
  width: 100%;
  justify-content: center;
  transition: all 0.2s ease;
}

/* Customize PrimeVue Button spacing */
:deep(.button-list-item .p-button-label) {
  flex: 1;
  text-align: center;
}

:deep(.button-list-item .p-button-icon) {
  margin-right: 8px;
}

/* Center align button content */
:deep(.button-list-item .p-button-content) {
  justify-content: center;
}

/* Responsive: Stack on tablet and below */
@media (max-width: 768px) {
  .button-list-container {
    flex-direction: column;
    gap: 10px;
  }

  .button-list-label {
    flex: 1 1 100%;
    text-align: left;
    padding-top: 0;
  }

  .field-label {
    font-size: 0.875rem;
  }

  .button-list-content {
    width: 100%;
  }
}

/* Responsive: Smaller gap on mobile */
@media (max-width: 480px) {
  .button-list-content {
    gap: 6px;
  }
}
</style>
